import { GhostButton, Loader, SectionMessage } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useInformationRequest } from "./hooks";
import { InformationRequestItemView } from "./InformationRequestItemView";
import { InformationRequestItemAnswer } from "./types";
import { applyItemAnswer } from "./utils";

type Props = {
  caseId: string;
  requestId: string;
  unansweredItemIds: string[];
  onItemsAnswered: (itemIds: string[]) => void;
  onItemUnanswered: (itemId: string) => void;
  onListRefresh: () => Promise<unknown>;
};

export function InformationRequestSection({
  caseId,
  requestId,
  unansweredItemIds,
  onItemsAnswered,
  onItemUnanswered,
  onListRefresh,
}: Props) {
  const { t } = useTranslation();
  const { data, error, isLoading, mutate } = useInformationRequest(
    caseId,
    requestId
  );

  useEffect(() => {
    const answeredItemIds = (data?.items ?? [])
      .filter((item) => item.is_answered && unansweredItemIds.includes(item.id))
      .map((item) => item.id);
    if (answeredItemIds.length) {
      onItemsAnswered(answeredItemIds);
    }
  }, [data, unansweredItemIds, onItemsAnswered]);

  if (isLoading) {
    return <Loader type="spinner" size="default" />;
  }

  if (error || !data) {
    return (
      <SectionMessage
        type="danger"
        testID="information-request-section-error"
        sectionTitle={t("Unable to load some of your requests")}
        actions={
          <GhostButton size="small" onPress={() => mutate()}>
            <Trans>Try again</Trans>
          </GhostButton>
        }
      />
    );
  }

  const items = [...data.items].sort((a, b) => a.order - b.order);
  const handleAnswered = (answer: InformationRequestItemAnswer) =>
    mutate((current) => applyItemAnswer(current, answer), {
      revalidate: false,
    });

  const handleRequestUpdated = () => mutate();

  return (
    <section
      className={styles.request}
      data-testid={`information-request-group-${requestId}`}
    >
      {items.map((item, index) => (
        <InformationRequestItemView
          key={item.id}
          caseId={caseId}
          item={item}
          itemNumber={index + 1}
          isMarkedUnanswered={unansweredItemIds.includes(item.id)}
          onAnswered={handleAnswered}
          onDocumentsChanged={() => mutate()}
          onItemUnanswered={() => onItemUnanswered(item.id)}
          onRequestUpdated={handleRequestUpdated}
          onListRefresh={onListRefresh}
        />
      ))}
    </section>
  );
}

const styles = {
  request: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-8)",
  }),
};
